import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
} from '@angular/core';
import { Booking, BookingChange, newGuideInterface } from '../../core/models';
import { ArchiveSearchStore } from '../../core/archive-search.store';
import { getLatestChange } from '../../core/booking-from.utils';
import { TimestampToDatePipe } from '../../core/timestamp-to-date.pipe';

@Component({
  selector: 'app-archive-listing',
  imports: [CommonModule, TimestampToDatePipe],
  standalone: true,
  template: `
    <div
      class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3"
    >
      <div class="flex items-center gap-3">
        <div>
          <span class="text-sm font-semibold text-gray-900">
            {{ b.ship.shipName }}
          </span>
          <span class="mx-1.5 text-gray-300">·</span>
          <span class="text-sm text-gray-600">
            {{ b.ship.companyName }}
          </span>

          <ng-container *ngIf="b.duration.startTime">
            <span class="mx-1.5 text-gray-300">·</span>
            <span class="text-sm text-gray-600" *ngIf="b.duration?.startTime">
              Start: {{ b.duration.startTime.toDate() | date: 'HH:mm' }}
            </span>
          </ng-container>

          <ng-container *ngIf="b.duration.endTime">
            <span class="mx-1.5 text-gray-300">·</span>

            <span class="text-sm text-gray-600" *ngIf="b.duration?.endTime">
              End: {{ b.duration.endTime.toDate() | date: 'HH:mm' }}
            </span>
          </ng-container>
        </div>
      </div>
      <div class="flex items-center gap-3 text-sm">
        <span
          *ngIf="b.duration?.tourCode"
          class="inline-flex items-center rounded-full bg-columbus-blue-50 px-2.5 py-0.5 text-xs font-medium text-columbus-blue-700"
        >
          Tourcode: {{ b.duration.tourCode }}
        </span>
        <span class="text-gray-500">
          {{ b.duration.date.toDate() | date: 'dd.MM.yyyy' }}
        </span>
      </div>
    </div>

    <!-- ── Card Body ── -->
    <div class="px-4 py-3 space-y-4 text-sm">
      <!-- Row 1: Cruise Details + Bus Info -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <!-- Cruise Details -->
        <div *ngIf="b.cruiseDetails">
          <h4
            class="text-xs font-semibold uppercase tracking-wide text-columbus-blue-700 mb-2"
          >
            Cruise Details
          </h4>
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt class="text-gray-500">Manager</dt>
            <dd class="text-gray-900">
              {{ b.cruiseDetails.cruiseManager }}
              <span
                *ngIf="b.cruiseDetails.cruiseManagerTel"
                class="ml-1 text-gray-400"
              >
                ({{ b.cruiseDetails.cruiseManagerTel }})
              </span>
            </dd>

            <dt class="text-gray-500">Rechnungs Nr.</dt>
            <dd class="text-gray-900">
              {{ b.cruiseDetails.invoiceNumber || '—' }}
            </dd>

            <dt class="text-gray-500">Anlegestelle</dt>
            <dd class="text-gray-900">
              {{ b.cruiseDetails.jetty || '—' }}
            </dd>

            <dt class="text-gray-500">PAX Ship</dt>
            <dd class="text-gray-900">
              {{ b.cruiseDetails.paxShip || '-' }}
            </dd>

            <dt class="text-gray-500">PAX Tour</dt>
            <dd class="text-gray-900">
              {{ b.cruiseDetails.paxTour || '-' }}
            </dd>
          </dl>
        </div>

        <!-- Bus Info -->
        <div *ngIf="b.busInfos">
          <h4
            class="text-xs font-semibold uppercase tracking-wide text-columbus-blue-700 mb-2"
          >
            Bus Info
          </h4>
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt class="text-gray-500">Company</dt>
            <dd class="text-gray-900">
              {{ b.busInfos.busCompany || '—' }}
            </dd>

            <ng-container *ngIf="b.driverInfo?.orderNumber">
              <dt class="text-gray-500">Order Nr.</dt>
              <dd class="text-gray-900">{{ b.driverInfo?.orderNumber }}</dd>
            </ng-container>
            <ng-container *ngIf="b.driverInfo?.customerNumber">
              <dt class="text-gray-500">Customer Nr.</dt>
              <dd class="text-gray-900">{{ b.driverInfo?.customerNumber }}</dd>
            </ng-container>
            <ng-container *ngIf="b.busInfos.selectedBuses">
              <dt class="text-gray-500">Busse:</dt>
              <dd class="text-gray-900"></dd>

              <ul
                class="text-end"
                *ngFor="let bus of b.busInfos.selectedBuses | keyvalue"
              >
                <ng-container *ngIf="bus.value > 0">
                  <li class="w-full justify-start text-start">
                    <span class="text-gray-900 pr-0.5">•</span
                    ><span class="text-gray-500">{{ bus.key }}:</span>
                    {{ bus.value }}
                  </li>
                </ng-container>
              </ul>
            </ng-container>
          </dl>

          <!-- Driver Details -->
          <div *ngIf="hasEntries(b.driverInfo?.drivers)" class="mt-2">
            <h5 class="text-xs font-medium text-gray-400 mb-1">Drivers</h5>
            <div
              *ngFor="let driver of b.driverInfo!.drivers! | keyvalue"
              class="text-sm text-gray-700"
            >
              {{ driver.value.driverName }}
              <span *ngIf="driver.value.driverTel" class="text-gray-400">
                — {{ driver.value.driverTel }}
              </span>
            </div>
          </div>
        </div>

        <div *ngIf="b.cruiseDetails">
          <h4
            class="text-xs font-semibold uppercase tracking-wide text-columbus-blue-700 mb-2"
          >
            Document Details
          </h4>
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt class="text-gray-500">Buchungs ID</dt>
            <dd class="text-gray-900">
              {{ b.id }}
            </dd>

            <dt class="text-gray-500">Erstellt</dt>
            <dd class="text-gray-900 font-medium">
              {{ b.createdDate | timestampToDate | date: 'dd.MM.YYYY' }}
            </dd>

            <dt class="text-gray-500">Ersteller</dt>
            <dd class="text-gray-900">
              {{ b.userCreatorName }}
            </dd>

            <ng-container *ngIf="getLatestBookingChange(b) as latestChange">
              <dt class="text-gray-500">Bearbeitet</dt>
              <dd class="text-gray-900">
                {{
                  latestChange.timestamp
                    ? (latestChange.timestamp
                      | timestampToDate
                      | date: 'dd.MM.YYYY')
                    : 'Kein Datum verfügbar'
                }}
              </dd>

              <dt class="text-gray-500">Bearbeiter</dt>
              <dd class="text-gray-900">
                {{ latestChange.changeAuthor.name }}
              </dd>
            </ng-container>
          </dl>
        </div>
      </div>

      <!-- Row 2: Guides -->
      <div *ngIf="b.guides && getOrderedGuides(b).length > 0">
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2"
        >
          Guides
        </h4>
        <div class="space-y-2">
          <div
            *ngFor="let guide of getOrderedGuides(b); let i = index"
            class="rounded-md border px-3 py-2"
            [ngClass]="{
              'border-red-200 bg-red-50': guide.storno.isActive,
              'border-gray-100 bg-gray-50': !guide.storno.isActive,
            }"
          >
            <!-- Guide Header Row -->
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-columbus-blue-100 text-xs font-medium text-columbus-blue-700"
                >
                  {{ i + 1 }}
                </span>
                <span class="font-medium text-gray-900">
                  {{ guide.guideDetails.forename }}
                  {{ guide.guideDetails.surname }}
                </span>
                <span
                  *ngIf="guide.storno?.isActive"
                  class="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                >
                  Storno
                </span>
              </div>
              <div class="flex items-center gap-3 text-xs text-gray-500">
                <span *ngIf="guide.guideDetails?.tel">
                  {{ guide.guideDetails.tel }}
                </span>
                <span *ngIf="guide.guideDetails?.email">
                  {{ guide.guideDetails.email }}
                </span>
              </div>
            </div>

            <!-- Guide Detail Grid -->
            <div
              class="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-4 text-sm"
            >
              <!-- Tour -->
              <div *ngIf="guide.tour?.tourInfo">
                <span class="text-gray-500">Tour: </span>
                <span class="text-gray-900">
                  {{ guide.tour.tourInfo.tourName }}
                </span>
                <span
                  *ngIf="guide.tour.tourLanguage"
                  class="text-gray-400 ml-1"
                >
                  ({{ guide.tour.tourLanguage }})
                </span>
                <span
                  *ngIf="guide.tour.tourConfirm"
                  class="ml-1 inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700"
                >
                  ✓
                </span>
              </div>

              <!-- Location -->
              <div *ngIf="guide.positionDetails?.location">
                <span class="text-gray-500">Location: </span>
                <span class="text-gray-900">
                  {{ guide.positionDetails.location }}
                </span>
              </div>

              <!-- Stellzeit (Position Time) -->
              <div *ngIf="guide.positionDetails?.time">
                <span class="text-gray-500">Stellzeit: </span>
                <span class="text-gray-900">
                  {{ guide.positionDetails.time.toDate() | date: 'HH:mm' }}
                </span>
              </div>

              <!-- Languages -->
              <div *ngIf="guide.guideDetails?.languages">
                <span class="text-gray-500">Languages: </span>
                <span class="text-gray-900">
                  {{ getActiveLanguages(guide.guideDetails.languages) }}
                </span>
              </div>
            </div>

            <!-- Storno Details -->
            <div
              *ngIf="
                guide.storno?.isActive &&
                (guide.storno.storno || guide.storno.customStorno)
              "
              class="mt-1 text-xs text-red-600"
            >
              Storno: {{ guide.storno.customStorno || guide.storno.storno }}
            </div>

            <!-- Other Fields -->
            <div
              *ngIf="guide.other && hasEntries(guide.other)"
              class="mt-2 border-t border-gray-200 pt-2"
            >
              <div
                class="mt-1 grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3 text-sm"
              >
                <div *ngFor="let entry of guide.other | keyvalue; index as i">
                  <ng-container *ngIf="entry.value">
                    <span class="text-gray-500">Sonstiges {{ i + 1 }} </span>

                    <span class="text-gray-900">{{ entry.value }}</span>
                  </ng-container>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: Footer meta -->
      <div
        class="flex flex-wrap justify-end items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-400"
      ></div>
    </div>
  `,
  styles: ``,
})
export class ArchiveListingComponent {
  @Input({ required: true }) b!: Booking;
  @Input({ required: true }) store!: ArchiveSearchStore;

  getOrderedGuides(booking: Booking): newGuideInterface[] {
    if (!booking.guides) return [];

    const guides = booking.guides;

    if (booking.guideOrder?.length) {
      return booking.guideOrder
        .filter((key) => guides[key] !== undefined)
        .map((key) => guides[key]);
    }

    return Object.values(guides);
  }

  getActiveLanguages(languages: Record<string, boolean>): string {
    return (
      Object.entries(languages)
        .filter(([, isActive]) => isActive)
        .map(([lang]) => lang)
        .join(', ') || '—'
    );
  }

  hasEntries(record: Record<string | number, unknown> | undefined): boolean {
    return !!record && Object.keys(record).length > 0;
  }

  getLatestBookingChange(booking: Booking): BookingChange | undefined {
    return getLatestChange(booking);
  }
}
